import React, { useEffect, useState, useMemo } from "react";
import Page from "./Page";
import RecurringTask from "../components/tasks/recurringTask";
import AppFooter from "../components/footer/AppFooter";
import {
  CircularProgress,
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Box,
  Chip,
  IconButton,
} from "@mui/material";
import {
  Search,
  EventRepeat,
  Replay,
  CalendarMonth,
  ArrowBack,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useRecurringTasksAPIClient from "../api/recurringTasks/hooks/useRecurringTasksAPIClient";
import { setRecurringTasks } from "../redux/actions/recurringTasks";
import { dispatch } from "../redux/store";
import { paletteTheme } from "../theme";
import HelpLink from "../components/help/HelpLink";

const RecurringTasksPage = () => {
  const navigate = useNavigate();
  const authenticated = useSelector((state) => state.user.authenticated);
  const { recurringTasks } = useSelector((state) => state.recurringTasks);
  const { taskSet } = useSelector((state) => state.tasks);
  const { getRecurringTasks } = useRecurringTasksAPIClient();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setLoading(true);
    if (authenticated) {
      const requestRecurringTasks = async () => {
        const recurringTasks = await getRecurringTasks();
        dispatch(setRecurringTasks(recurringTasks));
        setLoading(false);
      };
      void requestRecurringTasks();
    }
  }, [authenticated]);

  const filteredTasks = useMemo(() => {
    if (!recurringTasks || !Array.isArray(recurringTasks)) return [];
    if (!searchTerm.trim()) return recurringTasks;

    const lowerSearch = searchTerm.toLowerCase();
    return recurringTasks.filter((task) =>
      task.name?.toLowerCase().includes(lowerSearch),
    );
  }, [recurringTasks, searchTerm]);

  const stats = useMemo(() => {
    if (!recurringTasks || !Array.isArray(recurringTasks)) {
      return { total: 0, fixedSchedule: 0, afterCompletion: 0 };
    }
    const fixedSchedule = recurringTasks.filter(
      (t) => !t.frequency?.startIntervalOnCompletion,
    ).length;
    const afterCompletion = recurringTasks.filter(
      (t) => t.frequency?.startIntervalOnCompletion,
    ).length;
    return {
      total: recurringTasks.length,
      fixedSchedule,
      afterCompletion,
    };
  }, [recurringTasks]);

  return (
    <Page className="recurring-tasks-page" pageKey="recurring-tasks">
      <Box className="recurring-tasks-page__header">
        <IconButton
          onClick={() => navigate("/tasks")}
          className="recurring-tasks-page__back-button"
          aria-label="back to tasks"
        >
          <ArrowBack />
        </IconButton>
        <EventRepeat className="recurring-tasks-page__header-icon" />
        <Typography
          variant="h4"
          component="h1"
          className="recurring-tasks-page__title"
        >
          Recurring Tasks
        </Typography>
        <HelpLink
          to="/about/features/tasks/recurring-tasks"
          color="white"
          size="large"
          className="recurring-tasks-page__help-link"
        />
      </Box>

      <Grid
        container
        justifyContent="center"
        className="recurring-tasks-page__container"
      >
        {loading ? (
          <CircularProgress
            size={40}
            style={{
              color: paletteTheme.palette.white.main,
              marginTop: "48px",
            }}
          />
        ) : (
          <>
            {/* Stats */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  icon={<EventRepeat sx={{ color: "white !important" }} />}
                  label={`${stats.total} total`}
                  sx={{
                    bgcolor: "#197b68",
                    color: "white !important",
                    fontWeight: "bold",
                  }}
                />
                <Chip
                  icon={<CalendarMonth sx={{ color: "#2e7d32 !important" }} />}
                  label={`${stats.fixedSchedule} fixed`}
                  sx={{ bgcolor: "#e8f5e9", color: "#2e7d32" }}
                />
                <Chip
                  icon={<Replay sx={{ color: "#1565c0 !important" }} />}
                  label={`${stats.afterCompletion} after completion`}
                  sx={{ bgcolor: "#e3f2fd", color: "#1565c0" }}
                />
              </Box>
            </Grid>

            {/* Search */}
            <Grid item xs={12} display="flex" justifyContent="center">
              <TextField
                placeholder="Search recurring tasks..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value.slice(0, 50))}
                inputProps={{ maxLength: 50 }}
                sx={{
                  mb: 2,
                  width: { xs: "100%", sm: "400px" },
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "rgba(255,255,255,0.9)",
                    borderRadius: 2,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Task list */}
            <Grid
              item
              xs={12}
              className="recurring-tasks-page__task-container scroller"
            >
              {filteredTasks.length > 0 ? (
                filteredTasks.map((task) => {
                  const taskId =
                    typeof task.taskId === "string"
                      ? parseInt(task.taskId)
                      : task.taskId;
                  return (
                    <RecurringTask
                      task={task}
                      taskIsCurrentlyInBacklog={!!taskSet[taskId]}
                      key={task.taskId}
                    />
                  );
                })
              ) : searchTerm ? (
                <Typography sx={{ color: "white", textAlign: "center", py: 4 }}>
                  No recurring tasks match "{searchTerm}"
                </Typography>
              ) : (
                <Typography
                  variant="body1White"
                  className="recurring-tasks-page__no-tasks-label"
                >
                  You have no recurring tasks
                </Typography>
              )}
            </Grid>
          </>
        )}
      </Grid>
      <AppFooter page="recurring-tasks" />
    </Page>
  );
};

export default RecurringTasksPage;
